/**
 * Inventory navigation shared by the sidebar, in-page section nav, and routes.
 *
 * Lookup is per game. Add, History, and Parse stay one shared workflow so a
 * draft can mix Magic, Pokémon, and Lorcana.
 */

export const INVENTORY_LOOKUP_GAMES = [
  { id: 'mtg', label: 'Magic', path: '/inventory/mtg' },
  { id: 'pokemon', label: 'Pokémon', path: '/inventory/pokemon' },
  { id: 'lorcana', label: 'Lorcana', path: '/inventory/lorcana' },
] as const;

export type InventoryLookupGame = (typeof INVENTORY_LOOKUP_GAMES)[number]['id'];

export const DEFAULT_INVENTORY_LOOKUP_PATH = INVENTORY_LOOKUP_GAMES[0].path;

/** Sealed value lookup. Same Inventory section as the per-game Lookup pages. */
export const INVENTORY_SEALED_LOOKUP = {
  id: 'sealed',
  label: 'Sealed',
  path: '/inventory/sealed',
} as const;

export const INVENTORY_SHARED_SECTIONS = [
  { id: 'add', label: 'Add Inventory', path: '/inventory/add' },
  { id: 'history', label: 'Batch History', path: '/inventory/history' },
  { id: 'parse', label: 'Parse List', path: '/inventory/parse' },
] as const;

const ADD_INVENTORY_PATH = '/inventory/add';

const LOOKUP_GAME_IDS = new Set<string>(INVENTORY_LOOKUP_GAMES.map((game) => game.id));

const LOOKUP_GAME_ALIASES: Record<string, InventoryLookupGame> = {
  mtg: 'mtg',
  magic: 'mtg',
  'magic single': 'mtg',
  'magic the gathering': 'mtg',
  'magic: the gathering': 'mtg',
  pokemon: 'pokemon',
  'pokémon': 'pokemon',
  'pokemon tcg': 'pokemon',
  lorcana: 'lorcana',
  'lorcana tcg': 'lorcana',
  'disney lorcana': 'lorcana',
};

export function isInventoryLookupGame(value: string | undefined): value is InventoryLookupGame {
  return value != null && LOOKUP_GAME_IDS.has(value);
}

/** Game segment of `/inventory/:game`, or null on shared Add / History / Parse routes. */
export function inventoryLookupGameFromPathname(pathname: string): InventoryLookupGame | null {
  const [section, game] = pathname.split('/').filter(Boolean);
  if (section !== 'inventory' || !isInventoryLookupGame(game)) {
    return null;
  }
  return game;
}

/**
 * Add Inventory stays one route. `?game=` only defaults the search tab;
 * it does not create a per-game draft.
 */
export function addInventoryHref(game?: string | null): string {
  if (game != null && isInventoryLookupGame(game)) {
    return `${ADD_INVENTORY_PATH}?game=${game}`;
  }
  return ADD_INVENTORY_PATH;
}

/** Shared section href. Add picks up the Lookup game currently on screen. */
export function inventorySharedHref(path: string, pathname: string): string {
  if (path === ADD_INVENTORY_PATH) {
    return addInventoryHref(inventoryLookupGameFromPathname(pathname));
  }
  return path;
}

export function inventoryLookupPath(game: InventoryLookupGame): string {
  return `/inventory/${game}`;
}

/**
 * Canonical lookup game for a `products.product_line` (or game key).
 * Blank, "all", and unknown lines return null so callers can skip the link.
 */
export function lookupGameForProductLine(value: string | null | undefined): InventoryLookupGame | null {
  const raw = (value || '').trim().toLowerCase();
  if (!raw || raw === 'all' || raw === 'any') return null;
  const alias = LOOKUP_GAME_ALIASES[raw];
  if (alias) return alias;
  if (raw.includes('pokémon') || raw.includes('pokemon')) return 'pokemon';
  if (raw.includes('lorcana')) return 'lorcana';
  if (raw.includes('magic')) return 'mtg';
  return null;
}

export function isMagicProductLine(productLine: string | null | undefined): boolean {
  return lookupGameForProductLine(productLine) === 'mtg';
}

export function inventoryLookupLabel(game: InventoryLookupGame): string {
  return INVENTORY_LOOKUP_GAMES.find((entry) => entry.id === game)?.label ?? 'Magic';
}

export interface InventoryCopyLookupTarget {
  productLine?: string | null;
  productName?: string | null;
  setName?: string | null;
  cardNumber?: string | null;
  condition?: string | null;
  printing?: string | null;
  language?: string | null;
}

/**
 * Lookup URL for one copy. Null when product_line is not a known game.
 * `open=1` asks Lookup to open the first matching row.
 */
export function inventoryCopyLookupHref(target: InventoryCopyLookupTarget): string | null {
  const game = lookupGameForProductLine(target.productLine);
  if (!game) return null;
  const params = new URLSearchParams();
  if (target.productName) params.set('name', target.productName);
  if (target.setName) params.set('set', target.setName);
  if (target.cardNumber) params.set('number', target.cardNumber);
  if (target.condition) params.set('condition', target.condition);
  if (target.printing) params.set('printing', target.printing);
  if (target.language) params.set('language', target.language);
  params.set('open', '1');
  return `${inventoryLookupPath(game)}?${params.toString()}`;
}
